import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaTrash, FaBoxOpen, FaPills, FaStore, FaMoneyBillWave } from "react-icons/fa";
import { HiOutlineSearch } from "react-icons/hi";
import { FiArrowLeft, FiPlus, FiSave, FiX, FiCheck } from "react-icons/fi";
import Swal from "sweetalert2";

export default function CreatePaket() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [role, setRole] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [name, setName] = useState("");
  const [hargaJual, setHargaJual] = useState("");
  const [tuslah, setTuslah] = useState("");
  const [embalase, setEmbalase] = useState("");
  const [loadingDrugs, setLoadingDrugs] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    const userRole = (localStorage.getItem("role") || "").toLowerCase();
    setRole(userRole);

    if (userRole === "manager") {
      const outletId = localStorage.getItem("id_outlet");
      if (outletId) {
        setSelectedOutlet(outletId);
        fetchDrugs(outletId);
      }
    } else if (["owner", "admin", "superadmin"].includes(userRole)) {
      fetchOutlets();
    }
  }, []);

  const fetchOutlets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) setOutlets(data.data || []);
    } catch (error) {
      console.error("Error fetching outlets:", error);
    }
  };

  const fetchDrugs = async (outletId, searchQuery = "") => {
    if (!outletId) return;
    setLoadingDrugs(true);
    try {
      const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/v1/drug`);
      url.searchParams.append("id_outlet", outletId);
      url.searchParams.append("per_page", 50);
      if (searchQuery) url.searchParams.append("search", searchQuery);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        setDrugs(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching drugs:", error);
    } finally {
      setLoadingDrugs(false);
    }
  };

  const handleOutletChange = (e) => {
    const outletId = e.target.value;
    setSelectedOutlet(outletId);
    setSelectedItems([]);
    if (outletId) {
      fetchDrugs(outletId);
    } else {
      setDrugs([]);
    }
  };

  const handleAdd = (item) => {
    if ((item.stock || 0) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Stok Obat Habis",
        text: `Obat "${item.name}" memiliki stok 0 dan tidak dapat ditambahkan ke paket.`,
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }
    if (!selectedItems.find((i) => i.id === item.id)) {
      setSelectedItems([...selectedItems, { ...item, qty: 1 }]);
    }
  };

  const handleRemove = (id) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== id));
  };

  const handleQtyChange = (id, qty) => {
    if (qty === "") {
      setSelectedItems(selectedItems.map((item) => (item.id === id ? { ...item, qty: "" } : item)));
    } else {
      const newQty = Math.max(1, Number(qty));
      setSelectedItems(selectedItems.map((item) => (item.id === id ? { ...item, qty: newQty } : item)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !selectedOutlet || !hargaJual || selectedItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Form Belum Lengkap",
        text: "Pilih outlet, isi nama paket, harga jual, dan pilih minimal 1 obat!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoadingSubmit(true);

    const payload = {
      name,
      outlet_id: Number(selectedOutlet),
      harga_jual: Number(hargaJual) || 0,
      tuslah: Number(tuslah) || 0,
      embalase: Number(embalase) || 0,
      items: selectedItems.map((item) => ({
        drug_id: Number(item.id),
        qty: Number(item.qty) || 1,
      })),
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/pack`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        await Swal.fire({
          title: "Berhasil Disimpan!",
          text: `Paket resep "${name}" berhasil dibuat.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });
        navigate("/obat-paket/paket");
      } else {
        Swal.fire({
          title: "Gagal Menyimpan",
          text: result.message || "Terjadi kesalahan server.",
          icon: "error",
          customClass: { popup: "rounded-3xl" },
        });
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      Swal.fire({
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan koneksi server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const filteredDrugs = drugs.filter((item) =>
    (item.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/obat-paket/paket")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Paket Resep Baru
            </h1>
            <p className="text-xs text-gray-500">
              Kombinasikan beberapa obat master menjadi satu paket resep racikan
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="space-y-6 text-xs">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-3xl p-8 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Nama Paket */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nama Paket Resep *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Paket Batuk Flu Dewasa / Racikan Paracetamol"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                required
              />
            </div>

            {/* Outlet Cabang */}
            {["owner", "admin", "superadmin"].includes(role) && (
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <FaStore className="text-emerald-600" /> Outlet Cabang *
                </label>
                <select
                  value={selectedOutlet}
                  onChange={handleOutletChange}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  required
                >
                  <option value="">-- Pilih Outlet Cabang --</option>
                  {outlets.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Harga Jual Paket */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Harga Jual Paket (Rp) *
              </label>
              <input
                type="number"
                value={hargaJual}
                onChange={(e) => setHargaJual(e.target.value)}
                placeholder="0"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-mono font-bold text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                required
              />
            </div>

            {/* Tuslah */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Biaya Tuslah (Rp)
              </label>
              <input
                type="number"
                value={tuslah}
                onChange={(e) => setTuslah(e.target.value)}
                placeholder="0"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-mono font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Embalase */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Biaya Embalase / Kemasan (Rp)
              </label>
              <input
                type="number"
                value={embalase}
                onChange={(e) => setEmbalase(e.target.value)}
                placeholder="0"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-mono font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>
        </motion.div>

        {/* Drug Selection Matrix */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Item Selector */}
          <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-700 flex items-center gap-2">
              <FaPills className="text-emerald-600 w-4 h-4" />
              <span>Pilih Obat Dari Katalog Master</span>
            </h3>

            <div className="relative">
              <HiOutlineSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Cari obat..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-2xl">
              {loadingDrugs ? (
                <div className="p-6 text-center text-gray-400">Memuat obat...</div>
              ) : filteredDrugs.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  {selectedOutlet ? "Obat tidak ditemukan" : "Pilih outlet terlebih dahulu"}
                </div>
              ) : (
                filteredDrugs.map((item) => {
                  const isSelected = selectedItems.some((i) => i.id === item.id);
                  const isOutOfStock = (item.stock || 0) <= 0;
                  return (
                    <div
                      key={item.id}
                      className="p-3 flex items-center justify-between hover:bg-emerald-50/30 transition-colors"
                    >
                      <div>
                        <p className="font-bold text-gray-900">{item.name}</p>
                        <p className="text-[10px] font-semibold flex items-center gap-2">
                          <span className="text-gray-400">Dosis: {item.dosis || "-"}</span>
                          <span
                            className={`px-1.5 py-0.5 rounded font-mono font-bold ${
                              isOutOfStock
                                ? "bg-rose-50 text-rose-600 border border-rose-200"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            Stok: {item.stock || 0}
                          </span>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAdd(item)}
                        disabled={isSelected || isOutOfStock}
                        className={`px-3 py-1.5 rounded-xl font-bold transition-all text-xs ${
                          isOutOfStock
                            ? "bg-rose-50 text-rose-400 border border-rose-200 cursor-not-allowed"
                            : isSelected
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                        }`}
                      >
                        {isOutOfStock ? "Stok Habis" : isSelected ? "Terpilih" : "+ Tambah"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Selected Items */}
          <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-700 flex items-center gap-2">
              <FaBoxOpen className="text-emerald-600 w-4 h-4" />
              <span>Daftar Obat Dalam Paket ({selectedItems.length})</span>
            </h3>

            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-2xl">
              {selectedItems.length === 0 ? (
                <div className="p-12 text-center text-gray-400 font-semibold">
                  Belum ada obat ditambahkan ke paket ini
                </div>
              ) : (
                selectedItems.map((item) => (
                  <div key={item.id} className="p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">{item.name}</p>
                      <p className="text-[10px] text-gray-400 font-mono">
                        Harga: Rp {(item.harga_jual || 0).toLocaleString("id-ID")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => handleQtyChange(item.id, e.target.value)}
                        className="w-16 p-1.5 border border-gray-200 rounded-xl text-center font-bold text-emerald-800"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemove(item.id)}
                        className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="pt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/obat-paket/paket")}
            className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
          >
            Batal
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loadingSubmit}
            className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
          >
            <FiSave className="w-4 h-4" />
            <span>{loadingSubmit ? "Menyimpan..." : "Simpan Paket Resep"}</span>
          </motion.button>
        </div>
      </form>
    </div>
  );
}
